import { useState } from 'react';
import { useSessionStore } from '../store/sessionStore';
import { Search, ChevronRight, Circle, AlertTriangle, SearchX } from 'lucide-react';
import { getRelativeTime } from '../utils/time';
import './SessionPanel.css';

function groupByRecency(sessions: Record<string, ReturnType<typeof useSessionStore.getState>['sessions'][string]>) {
  const now = Date.now();
  const DAY = 24 * 60 * 60 * 1000;
  const active: typeof sessions = {};
  const recent24h: typeof sessions = {};
  const older: typeof sessions = {};

  for (const [id, session] of Object.entries(sessions)) {
    if (session.last_event_at > now - 60_000) active[id] = session;
    else if (session.last_event_at > now - DAY) recent24h[id] = session;
    else older[id] = session;
  }
  return { active, recent24h, older };
}

export function SessionPanel() {
  const [collapsed, setCollapsed] = useState({ active: false, recent24h: false, older: true });
  const [search, setSearch] = useState('');
  const { sessions, activeSessionId, selectSession, unacknowledgedAlerts } = useSessionStore();

  const grouped = groupByRecency(sessions);
  const hasSessions = Object.keys(sessions).length > 0;

  function filterSessions(s: Record<string, ReturnType<typeof useSessionStore.getState>['sessions'][string]>) {
    if (!search) return s;
    return Object.fromEntries(
      Object.entries(s).filter(([, session]) =>
        session.workflow_name.toLowerCase().includes(search.toLowerCase())
      )
    );
  }

  function renderSessionCard(id: string, session: typeof sessions[string]) {
    const isActive = id === activeSessionId;
    const anomalyCount = unacknowledgedAlerts.filter((a) => a.trace_id === id).length;
    const statusColor =
      session.status === 'anomaly'
        ? 'var(--accent-red)'
        : session.status === 'stalled'
        ? 'var(--accent-amber)'
        : 'var(--accent-green)';

    return (
      <div
        key={id}
        className={`session-card ${isActive ? 'session-card--active' : ''}`}
        onClick={() => selectSession(id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && selectSession(id)}
      >
        <div className="session-card-header">
          <Circle size={6} fill={statusColor} color={statusColor} />
          <span className="session-card-name">{session.workflow_name}</span>
        </div>
        <div className="session-card-meta">
          <span>{session.agent_count} agents</span>
          <span className="dot">·</span>
          <span>{session.event_count} events</span>
        </div>
        <div className="session-card-footer">
          <span className="session-card-time">{getRelativeTime(session.last_event_at)}</span>
          {anomalyCount > 0 && (
            <span className="session-card-alert-badge">
              <AlertTriangle size={10} />
              {anomalyCount}
            </span>
          )}
        </div>
      </div>
    );
  }

  function renderGroup(label: string, key: 'active' | 'recent24h' | 'older', s: Record<string, ReturnType<typeof useSessionStore.getState>['sessions'][string]>) {
    const filtered = filterSessions(s);
    const entries = Object.entries(filtered);
    if (entries.length === 0) return null;

    return (
      <div className="session-group">
        <button
          className="session-group-header"
          onClick={() => setCollapsed((c) => ({ ...c, [key]: !c[key] }))}
        >
          <ChevronRight size={12} className={`chevron ${collapsed[key] ? '' : 'open'}`} />
          <span>{label}</span>
          <span className="session-group-count">{entries.length}</span>
        </button>
        {!collapsed[key] && (
          <div className="session-group-list">
            {entries.map(([id, session]) => renderSessionCard(id, session))}
          </div>
        )}
      </div>
    );
  }

  if (!hasSessions) {
    return (
      <aside className="session-panel">
        <div className="session-panel-empty">
          <SearchX size={32} className="empty-icon" />
          <p className="empty-title">No sessions yet</p>
          <p className="empty-subtitle">Install the SDK and run your first agent workflow</p>
        </div>
      </aside>
    );
  }

  return (
    <aside className="session-panel">
      <div className="session-search-wrapper">
        <Search size={13} className="session-search-icon" />
        <input
          type="text"
          className="session-search-input"
          placeholder="Search sessions…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {renderGroup('Active Now', 'active', grouped.active)}
      {renderGroup('Last 24h', 'recent24h', grouped.recent24h)}
      {renderGroup('Older', 'older', grouped.older)}
    </aside>
  );
}
